import React, { useContext } from 'react'
import * as S from './styles'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory, useLocation } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { AmbulatoryAuthorizationDashboardContext } from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import {
  cpfMask,
  landlineMaskWithParenthesis,
  phoneMaskWithParenthesis
} from 'presentation/utils/masks'
import { GenderNewTranslated } from 'common/enum/gender'
import moment from 'moment-timezone'
import { MaritalStatusTranslated } from 'presentation/utils/default-marital-status'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { useFormik } from 'formik'
import { useServices } from 'presentation/hooks/use-services'
import { toast } from 'react-toastify'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import validationSchema from './validationSchema'

type LocationProps = {
  isEditing?: boolean
}

export const AmbulatoryPatientInfoLayout = WithLoading(
  ({ setIsLoading }: WithLoadingProps) => {
    const history = useHistory()
    const stateLocation = useLocation()?.state as LocationProps
    const patientService = useServices().patient
    const { state, dispatch } = useContext<any>(
      AmbulatoryAuthorizationDashboardContext
    )
    const { patient } = state
    if (!patient?.cpf) {
      history.push('/pedidos-autorizacao-ambulatorial/solicitacao')
    }

    function hasContactInfoChanged(patient: any, contactInfo: any): boolean {
      return (
        patient.phone !== contactInfo.phone ||
        patient.landline !== contactInfo.landlinePhone ||
        patient.email !== contactInfo.email
      )
    }

    const formik = useFormik({
      initialValues: {
        landlinePhone: patient.landline,
        phone: patient.phone,
        email: patient.email ?? ''
      },
      validationSchema: validationSchema,
      validateOnMount: true,
      onSubmit: async (values) => {
        if (hasContactInfoChanged(state.patient, values)) {
          try {
            setIsLoading(true)
            await patientService.updatePatientContact({
              patient_id: patient.patient_id,
              email: values.email,
              phone: values.phone?.toString().replace(/\D/g, ''),
              landline: values.landlinePhone?.toString().replace(/\D/g, '')
            })

            const updatedPatient = {
              ...state.patient,
              email: values.email,
              phone: values.phone?.toString().replace(/\D/g, ''),
              landline: values.landlinePhone?.toString().replace(/\D/g, '')
            }

            dispatch({
              type: 'PATIENT_INFO',
              payload: updatedPatient
            })
            history.push('convenio', { isEditing: stateLocation?.isEditing })
          } catch (error: any) {
            toast.error(error?.message)
          } finally {
            setIsLoading(false)
          }
        } else {
          history.push('convenio', { isEditing: stateLocation?.isEditing })
        }
      }
    }) as any

    return (
      <>
        <HeaderNew
          actualPageOnClick={() =>
            stateLocation?.isEditing ? history.push('resumo') : history.goBack()
          }
          actualPageTitle="Solicitar pedido ambulatorial"
        />

        <ContainerNew
          title="Dados do paciente"
          titleColor="neutral800"
          titleSize="large"
          titleWeight="bold"
          // style={{ paddingBottom: 0 }}
          primaryButton={
            <ButtonNew
              fullWidth
              size="large"
              onClick={formik.submitForm}
              disabled={formik.isSubmitting}
            >
              {stateLocation?.isEditing ? 'Salvar' : 'Próximo'}
            </ButtonNew>
          }
        >
          <S.Wrapper>
            <S.FormWrapper action="#" onSubmit={formik.handleSubmit}>
              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="CPF "
                name="cpf"
                value={patient.cpf}
                mask={cpfMask}
                disabled
                required
                requiredColor="neutral800"
              />

              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="Nome do paciente "
                name="name"
                value={patient.name}
                disabled
                required
                requiredColor="neutral800"
              />

              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="Data de nascimento "
                name="birthday"
                value={moment(patient.birthday).format('DD/MM/YYYY')}
                disabled
                required
                requiredColor="neutral800"
              />

              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="Sexo "
                value={
                  GenderNewTranslated[
                    patient.gender.toUpperCase() as keyof typeof GenderNewTranslated
                  ]
                }
                name="gender"
                disabled
                required
                requiredColor="neutral800"
              />

              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="Celular *"
                mask={phoneMaskWithParenthesis}
                name="phone"
                onChange={formik.handleChange}
                value={formik.values.phone}
                error={formik.touched.phone && formik.errors.phone}
              />

              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="Telefone fixo"
                name="landlinePhone"
                onChange={formik.handleChange}
                mask={landlineMaskWithParenthesis}
                value={formik.values.landlinePhone}
                error={
                  formik.touched.landlinePhone && formik.errors.landlinePhone
                }
              />

              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="E-mail "
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                required
                requiredColor="neutral800"
                error={formik.touched.email && formik.errors.email}
              />

              <TextFieldNew
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                label="Estado civil "
                value={
                  MaritalStatusTranslated[
                    patient.marital_status.toUpperCase() as keyof typeof MaritalStatusTranslated
                  ]
                }
                name="maritalStatus"
                disabled
                required
                requiredColor="neutral800"
              />
            </S.FormWrapper>
          </S.Wrapper>
        </ContainerNew>
      </>
    )
  }
)

export default AmbulatoryPatientInfoLayout
